
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UseFormReturn } from "react-hook-form";
import { JobFormValues } from "../../schemas/jobFormSchema";

interface AIContentGeneratorProps {
  form: UseFormReturn<JobFormValues>;
  clientName?: string;
}

export const AIContentGenerator = ({ form, clientName }: AIContentGeneratorProps) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateContent = async () => {
    try {
      setIsGenerating(true);
      
      // Get current form values
      const formValues = form.getValues();
      
      // Prepare data for the AI function
      const requestData = {
        title: formValues.title,
        company: formValues.company,
        location: formValues.location,
        type: formValues.type,
        clientName: clientName || "",
        additionalContext: "" // Could be expanded with additional context fields
      };
      
      toast.info("Generating job description and requirements...");
      
      const { data, error } = await supabase.functions.invoke('generate-job-content', {
        body: requestData
      });

      if (error) {
        console.error("Error generating content:", error);
        toast.error("Failed to generate content. Please try again.");
        return;
      }

      if (!data.success) {
        console.error("API reported error:", data.error);
        toast.error(data.error || "Failed to generate content");
        return;
      }

      // Update the form with the generated content
      form.setValue("description", data.content.description, {
        shouldValidate: true,
        shouldDirty: true,
      });
      
      form.setValue("requirements", data.content.requirements, {
        shouldValidate: true,
        shouldDirty: true,
      });

      toast.success("Job description and requirements generated!");
      
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex items-center justify-end py-2">
      <Button
        type="button"
        variant="outline"
        onClick={handleGenerateContent}
        disabled={isGenerating}
        className="text-sm gap-2"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            Generate with AI
          </>
        )}
      </Button>
    </div>
  );
};
